import { Currencies, Money, PaymentType } from '@afterpay/types'
import { useMemo } from 'react'
import { useItemsDataVault } from '../../../state'
import { ItemType } from '@afterpaytouch/portal-api/consumer/checkout/types'
import { addMoneys } from '@afterpay/utils/lib/money'
import { EnrichedItem, money } from '../../../model'

type SummaryDetailsViewModel = {
  downPaymentAmount: Money
  shippingAmount: Money
  totalDueTodayAmount: Money
  financedItems: EnrichedItem[]
  payInFullItems: EnrichedItem[]
  totalInterestAmount: Money
  totalAmount: Money
}

export const useSummaryDetailsViewModel = (): SummaryDetailsViewModel => {
  const data = useItemsDataVault()

  // exclude the trade-in item
  const financedItems = useMemo(() => {
    return Object.values(data).filter((item) => item.paymentType === PaymentType.PCL && item.childId === undefined)
  }, [data])

  const payInFullItems = useMemo(() => {
    return Object.values(data).filter((item) => item.paymentType === PaymentType.PBI)
  }, [data])

  const currency = financedItems[0]?.price?.currency ?? Currencies.AUD
  const shippingAmount = useMemo(() => {
    return payInFullItems.find((item) => item.type === ItemType.SHIPPING)?.price ?? money('0', currency)
  }, [payInFullItems])

  const downPaymentAmount = useMemo(() => {
    return financedItems.reduce((acc: Money, item: EnrichedItem): Money => {
      const sum = addMoneys({ moneys: [acc, item.downPaymentAmount ?? money('0', currency)] })
      return money(sum, currency)
    }, money('0', currency))
  }, [financedItems])

  const totalPayInFullAmount = useMemo(() => {
    return payInFullItems.reduce((acc: Money, item: EnrichedItem): Money => {
      const sum = addMoneys({ moneys: [acc, item.price ?? money('0', currency)] })
      return money(sum, currency)
    }, money('0', currency))
  }, [payInFullItems])

  const totalFinancedAmount = useMemo(() => {
    return financedItems.reduce((acc: Money, item: EnrichedItem): Money => {
      const sum = addMoneys({ moneys: [acc, item.price ?? money('0', currency)] })
      return money(sum, currency)
    }, money('0', currency))
  }, [financedItems])

  const totalDueTodayAmount = useMemo(
    () => money(addMoneys({ moneys: [downPaymentAmount, totalPayInFullAmount] }), currency),
    [downPaymentAmount, totalPayInFullAmount]
  )

  const totalInterestAmount = useMemo(() => {
    return financedItems.reduce((acc: Money, item: EnrichedItem): Money => {
      const sum = addMoneys({ moneys: [acc, item.interestAmount ?? money('0', currency)] })
      return money(sum, currency)
    }, money('0', currency))
  }, [financedItems])

  const totalAmount = useMemo(() => {
    return money(addMoneys({ moneys: [totalFinancedAmount, totalInterestAmount] }), currency)
  }, [totalFinancedAmount, totalInterestAmount])

  return {
    downPaymentAmount,
    shippingAmount,
    totalDueTodayAmount,
    financedItems,
    payInFullItems,
    totalInterestAmount,
    totalAmount,
  }
}
